import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';
import { useAppContext } from '../../../appComponents/AppContext';
import useBehaviorSubject from '../../../functions/hooks/useBehaviorSubject';
import { usePersistFn } from '../../../functions/hooks/usePersistFn';
import type { AppModalNavigation } from '../../../functions/navigation/utils/routeHelpers';
import AsyncStorage, { Key } from '../../../utils/asyncStorage';
import { makeDebug } from '../../../utils/makeDebug';

const kVersion = '1.0';

const debug = makeDebug('app:onboarding-v2:progress-tracking');

export type OnboardingPhase =
  | {
      type: 'auth';
      step:
        | 'phone'
        | 'sms-code-verify'
        | 'connect-phone'
        | 'connect-phone-sms-code-verify'
        | 'password';
    }
  | {
      type: 'basic-info';
      step: 'name-info' | 'email' | 'username';
      uid: string;
    }
  | {
      type: 'extra-info';
      step: 'invite-code' | 'avatar';
      uid: string;
    };

export type PhaseType = OnboardingPhase['type'];

export type OnboardingCompletionHandler = (
  navigation: AppModalNavigation,
) => void;

export type OnboardingProgressInfo = {
  version: string;
  phase: OnboardingPhase;
};

const onboardingProgress$ =
  AsyncStorage.behaviorSubject$<OnboardingProgressInfo>(
    Key.NewOnboardingProgress,
  );

const onboardingHasCompletion$ = AsyncStorage.behaviorSubject$<boolean>(
  Key.NewOnboardingHasCompletion,
);

export const useOnboardingProgress = () => {
  const [progress, setProgress] = useBehaviorSubject(onboardingProgress$);
  const { onboardingCompletionHandler } = useAppContext();

  const updateProgress = useCallback(
    (phase: OnboardingPhase | undefined) => {
      debug('progress updated to:', phase);
      setProgress(
        phase == null
          ? null
          : {
              version: kVersion,
              phase,
            },
      );
    },
    [setProgress],
  );

  const complete = useCallback(
    (navigation: AppModalNavigation) => {
      if (onboardingCompletionHandler.current != null) {
        onboardingCompletionHandler.current(navigation);
      } else {
        if (navigation.canGoBack()) {
          navigation.popToTop();
        }
        navigation.replace('home', {} as any);
      }
      updateProgress(undefined);
      onboardingHasCompletion$.next(true);
      onboardingCompletionHandler.current = undefined;
      debug('onboarding completed');
    },
    [onboardingCompletionHandler, updateProgress],
  );

  return { progress, updateProgress, complete };
};

export const useBeginOnboardingPhase = (phase: OnboardingPhase) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const target = useRef(phase).current;
  const { progress, updateProgress } = useOnboardingProgress();
  const previousPhase = useRef(progress?.phase).current;

  useEffect(() => {
    debug('begin phase/step: ', target);
    updateProgress(target);
  }, [target, updateProgress]);

  useEffect(() => {
    return navigation.addListener('beforeRemove', () => {
      debug('go back to previous phase/step: ', previousPhase);
      updateProgress(previousPhase);
    });
  }, [navigation, previousPhase, updateProgress]);
};

export const useOnboardingHasCompletion = () => {
  const [flag] = useBehaviorSubject(onboardingHasCompletion$);
  return flag;
};

export const useOnOnboardingCompleted = (callback: () => void) => {
  const stableCallback = usePersistFn(callback);
  useEffect(() => {
    const sub = onboardingHasCompletion$.subscribe(completed => {
      if (completed) {
        stableCallback();
      }
    });
    return () => sub.unsubscribe();
  }, [stableCallback]);
};
